import axios from 'axios';
import {IDriver} from 'app/shared/model/driver.model';

/**
 * Driver Suggestion Service
 * Contains synchronous API calls for driver search functionality
 */
class DriverSuggestionService {
  private apiUrl = 'api/drivers';

  /**
   * Get driver by id
   * @param id - ID of the driver
   * @returns promise resolving to the driver
   */
  async getDriver(id: string): Promise<IDriver> {
    const requestUrl = `${this.apiUrl}/${id}`;
    const response = await axios.get<IDriver>(`${requestUrl}?cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by home village
   * @param startVillageId - ID of the starting village
   * @param endVillageId - ID of the ending village
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriverByHomeVillage(startVillageId: string, endVillageId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-home-village?startVillageId=${startVillageId}&endVillageId=${endVillageId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by neighbor village
   * @param startVillageId - ID of the starting village
   * @param endVillageId - ID of the ending village
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriversByNeighborVillage(startVillageId: string, endVillageId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-neighbor-village?startVillageId=${startVillageId}&endVillageId=${endVillageId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by same council as villages
   * @param startVillageId - ID of the starting village
   * @param endVillageId - ID of the ending village
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriversBySameVillageCouncil(startVillageId: string, endVillageId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-same-council-village?startVillageId=${startVillageId}&endVillageId=${endVillageId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by home healthcare facility
   * @param startFacilityId - ID of the starting healthcare facility
   * @param endFacilityId - ID of the ending healthcare facility
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriversByHomeHealthcareFacility(startFacilityId: string, endFacilityId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-home-healthcare-facility?startFacilityId=${startFacilityId}&endFacilityId=${endFacilityId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by neighbor healthcare facility
   * @param startFacilityId - ID of the starting healthcare facility
   * @param endFacilityId - ID of the ending healthcare facility
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriversByNeighborHealthcareFacility(startFacilityId: string, endFacilityId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-neighbor-healthcare-facility?startFacilityId=${startFacilityId}&endFacilityId=${endFacilityId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }

  /**
   * Get drivers by same council as healthcare facilities
   * @param startFacilityId - ID of the starting healthcare facility
   * @param endFacilityId - ID of the ending healthcare facility
   * @param requiredLevel - Minimum vehicle level required (default is 1)
   * @param limit - Maximum number of drivers to return (default is 15)
   * @returns Promise resolving to an array of drivers
   */
  async getDriversBySameHealthcareFacilityCouncil(startFacilityId: string, endFacilityId: string, requiredLevel = 1, limit = 15): Promise<IDriver[]> {
    const requestUrl = `${this.apiUrl}/by-same-council-healthcare-facility?startFacilityId=${startFacilityId}&endFacilityId=${endFacilityId}&requiredLevel=${requiredLevel}&limit=${limit}`;
    const response = await axios.get<IDriver[]>(`${requestUrl}&cacheBuster=${new Date().getTime()}`);
    return response.data;
  }


  /**
   * Find community drivers for village to village journey
   */
  async findCommunityDriversForVillageToVillage(
    startVillageId: string,
    endVillageId: string,
    requiredLevel = 1,
  ): Promise<IDriver[]> {
    // Get home village drivers for the start and end villages
    const homeVillageDrivers = await this.getDriverByHomeVillage(startVillageId, endVillageId, requiredLevel, 15);

    // Get neighbor village drivers for start and end villages
    const neighborVillageDrivers = await this.getDriversByNeighborVillage(startVillageId, endVillageId, requiredLevel, 15);

    // Get driver with same council as the start and end villages
    const sameCouncilDrivers = await this.getDriversBySameVillageCouncil(startVillageId, endVillageId, requiredLevel, 15);

    // Combine all drivers in the order of preference
    const drivers = [...homeVillageDrivers, ...neighborVillageDrivers, ...sameCouncilDrivers];

    // Remove duplicates by driver id and return unique drivers
    return drivers.filter((driver, index, self) =>
      index === self.findIndex(d => d.id === driver.id)
    );
  }

  /**
   * Find community drivers for village to healthcare facility journey
   */
  async findCommunityDriversForVillageToHF(
    startVillageId: string,
    requiredLevel = 1,
  ): Promise<IDriver[]> {
    // Get home village drivers for the start village
    const homeVillageDrivers = await this.getDriverByHomeVillage(startVillageId, startVillageId, requiredLevel, 15);

    // Get neighbor village drivers for start village
    const neighborVillageDrivers = await this.getDriversByNeighborVillage(startVillageId, startVillageId, requiredLevel, 15);

    // Get driver with same council as the start village
    const sameCouncilDrivers = await this.getDriversBySameVillageCouncil(startVillageId, startVillageId, requiredLevel, 15);

    // Combine all drivers in the order of preference
    const drivers = [...homeVillageDrivers, ...neighborVillageDrivers, ...sameCouncilDrivers];

    // Remove duplicates by driver id and return unique drivers
    return drivers.filter((driver, index, self) =>
      index === self.findIndex(d => d.id === driver.id)
    );
  }

  /**
   * Find community drivers for facility to facility journey
   */
  async findCommunityDriversForFacilityToFacility(
    startFacilityId: string,
    endFacilityId: string,
    requiredLevel = 1,
  ): Promise<IDriver[]> {
    // Get home healthcare facility drivers for the start and end facilities
    const homeFacilityDrivers = await this.getDriversByHomeHealthcareFacility(startFacilityId, endFacilityId, requiredLevel, 15);

    // Get neighbor healthcare facility drivers for start and end facilities
    const neighborFacilityDrivers = await this.getDriversByNeighborHealthcareFacility(startFacilityId, endFacilityId, requiredLevel, 15);

    // Get driver with same council as the start and end facilities
    const sameCouncilDrivers = await this.getDriversBySameHealthcareFacilityCouncil(startFacilityId, endFacilityId, requiredLevel, 15);

    // Combine all drivers in the order of preference
    const drivers = [...homeFacilityDrivers, ...neighborFacilityDrivers, ...sameCouncilDrivers];

    // Remove duplicates by driver id and return unique drivers
    return drivers.filter((driver, index, self) =>
      index === self.findIndex(d => d.id === driver.id)
    );
  }
}

// Export singleton instance
export const driverSuggestionService = new DriverSuggestionService();
export default driverSuggestionService;
